import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { usePageTitle } from '../hooks/usePageTitle';
import { TabMenu, type TabItem } from '@themes/aegix';
import { SMSPageHeader as PageHeader } from '../components/layout/SMSPageHeader';
import {
  HiClipboardDocumentList,
  HiAcademicCap,
  HiShieldCheck,
  HiUserGroup,
  HiEnvelope,
  HiAdjustmentsHorizontal,
} from 'react-icons/hi2';
import { PlansList } from '../components/reunification/PlansList';
import { StaffList } from '../components/reunification/StaffList';
import { StudentsList } from '../components/reunification/StudentsList';
import { InvitationDashboard } from '../components/reunification/InvitationDashboard';
import { DistrictReadiness } from '../components/reporting/DistrictReadiness';
import { ReunificationContent } from '../components/admin';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ReunificationTab extends TabItem {
  subtitle: string;
}

const tabs: ReunificationTab[] = [
  {
    value: 'plans',
    label: 'Plans',
    icon: HiClipboardDocumentList,
    subtitle: 'Create, Activate, and Manage Reunification Plans',
    'data-e2e': 'tab-plans',
  },
  {
    value: 'staff',
    label: 'Staff',
    icon: HiAcademicCap,
    subtitle: 'Manage Staff Available within Reunification Plans',
    'data-e2e': 'tab-staff',
  },
  {
    value: 'students',
    label: 'Students',
    icon: HiUserGroup,
    subtitle: 'Browse Students and Invite Guardians to Aegix One',
    'data-e2e': 'tab-students',
  },
  {
    value: 'invitations',
    label: 'Invitations',
    icon: HiEnvelope,
    subtitle: 'Track Aegix One Invitations Sent to Guardians and Students',
    'data-e2e': 'tab-invitations',
  },
  {
    value: 'readiness',
    label: 'Readiness',
    icon: HiShieldCheck,
    subtitle: 'District-Wide Reunification Readiness Overview',
    'data-e2e': 'tab-readiness',
  },
  {
    value: 'configure',
    label: 'Configure',
    icon: HiAdjustmentsHorizontal,
    subtitle: 'Stations, zones, staff roles, signage',
  },
];

const tabContent: Record<string, React.FC> = {
  plans: PlansList,
  staff: StaffList,
  readiness: DistrictReadiness,
};

export function Reunification() {
  const { t } = useTranslation();
  usePageTitle(t('reunification.title', 'Reunification'));
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'plans';

  const localizedTabs = useMemo(
    () =>
      tabs.map((tab) => ({
        ...tab,
        label: t(`reunification.tab.${tab.value}`, tab.label),
        subtitle: t(`reunification.tabSubtitle.${tab.value}`, tab.subtitle),
      })),
    [t],
  );

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      next.delete('subtab');
      next.delete('id');
      next.delete('public_id');
      return next;
    });
  };

  const activeTabData = localizedTabs.find((t) => t.value === activeTab);
  const ActiveContent = tabContent[activeTab];

  return (
    <Box mx={-8} mt={-8}>
      <PageHeader
        icon={activeTabData?.icon as React.ElementType}
        title={activeTabData?.label || t('reunification.title', 'Reunification')}
        subtitle={activeTabData?.subtitle || t('reunification.subtitle', 'Reunify students with parents and guardians during emergency events.')}
        showSearch={true}
        showNotifications={true}
      />
      <TabMenu tabs={localizedTabs} activeTab={activeTab} onChange={handleTabChange} e2eTitle="ReunificationTabs" />

      {ActiveContent && <ActiveContent />}
      {activeTab === 'students' && <StudentsList />}
      {activeTab === 'invitations' && <InvitationDashboard />}
      {activeTab === 'configure' && <ReunificationContent />}
    </Box>
  );
}
